import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { saveAs as saveAsFile } from 'file-saver';

import { useCreateTrainingRegisterReportsFileGenerationProcessMutation } from 'components/hooks/trainingRegisterReportsFileGenerationProcessMutations';
import { useTrainingRegisterReportsFileGenerationProcessQuery } from 'components/hooks/trainingRegisterReportsFileGenerationProcessQueries';

export default function useReportGenerator() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const createMutation =
    useCreateTrainingRegisterReportsFileGenerationProcessMutation();
  const fileGenerationProcessId = createMutation.data?.data?.id;

  const { data: domainFileGenerationProcess, isError: isPollingError } =
    useTrainingRegisterReportsFileGenerationProcessQuery({
      fileGenerationProcessId,
    });

  const progress = (() => {
    if (createMutation.isIdle) return 'initial';
    if (createMutation.isError || isPollingError) return 'failed';
    if (!domainFileGenerationProcess?.data) return 'generating';
    const { status, url } = domainFileGenerationProcess.data.attributes;
    if (status === 'failed') return 'failed';
    if (status === 'queued' || status === 'working') return 'generating';
    if (status === 'completed') {
      if (url) {
        return 'downloadReady';
      } else {
        return 'noResults';
      }
    }
  })();

  const reportUrl = domainFileGenerationProcess?.data.attributes.url;

  const reportGenerator = { isOpen, progress, reportUrl };

  const removeProcessQuery = (id) => {
    if (id) {
      queryClient.removeQueries({
        queryKey: ['trainingRegisterReports', 'fileGenerationProcesses', id],
      });
    }
  };

  const endReportGeneration = () => {
    setIsOpen(false);
    removeProcessQuery(fileGenerationProcessId);
    createMutation.reset();
  };

  const startReportGeneration = ({ params }) => {
    setIsOpen(true);
    removeProcessQuery(fileGenerationProcessId);
    createMutation.mutate({
      fileGenerationProcessParams: params.training_register_report,
    });
  };

  const downloadReport = () => {
    if (reportUrl) {
      saveAsFile(reportUrl);
      endReportGeneration();
    }
  };

  return [
    reportGenerator,
    startReportGeneration,
    endReportGeneration,
    downloadReport,
  ];
}
